import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemIntegration = ({ tenantId, syncStatus }) => {
  const [systems] = useState([
    {
      id: 'pos-terminal-1',
      name: 'POS Terminal 1',
      type: 'pos',
      status: 'connected',
      lastSync: new Date(Date.now() - 120000),
      syncCount: 1247,
      errorCount: 3
    },
    {
      id: 'pos-terminal-2',
      name: 'POS Terminal 2',
      type: 'pos',
      status: 'connected',
      lastSync: new Date(Date.now() - 60000),
      syncCount: 892,
      errorCount: 1
    },
    {
      id: 'ecommerce',
      name: 'E-Commerce Platform',
      type: 'ecommerce',
      status: 'connected',
      lastSync: new Date(Date.now() - 300000),
      syncCount: 3421,
      errorCount: 12
    },
    {
      id: 'mobile-app',
      name: 'Mobile Application',
      type: 'mobile',
      status: 'connected',
      lastSync: new Date(Date.now() - 180000),
      syncCount: 567,
      errorCount: 0
    },
    {
      id: 'warehouse',
      name: 'Warehouse System',
      type: 'warehouse',
      status: 'disconnected',
      lastSync: new Date(Date.now() - 7200000),
      syncCount: 234,
      errorCount: 45
    }
  ]);

  const getSystemIcon = (type) => {
    switch (type) {
      case 'pos':
        return 'ShoppingCart';
      case 'ecommerce':
        return 'Globe';
      case 'mobile':
        return 'Smartphone';
      case 'warehouse':
        return 'Warehouse';
      default:
        return 'Server';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'disconnected':
        return 'text-danger';
      case 'syncing':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-success/10';
      case 'disconnected':
        return 'bg-danger/10';
      case 'syncing':
        return 'bg-primary/10';
      default:
        return 'bg-muted';
    }
  };

  const formatLastSync = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">System Integration</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Connected systems and their synchronization status
        </p>
      </div>

      {/* Systems Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {systems?.map((system) => (
          <div key={system?.id} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name={getSystemIcon(system?.type)} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{system?.name}</h3>
                  <p className="text-sm text-muted-foreground capitalize">{system?.type}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                getStatusBg(system?.status)
              } ${getStatusColor(system?.status)}`}>
                {system?.status?.toUpperCase()}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last Sync</span>
                <span className="font-medium text-foreground">
                  {formatLastSync(system?.lastSync)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Syncs</span>
                <span className="font-medium text-foreground">{system?.syncCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Error Count</span>
                <span className={`font-medium ${
                  system?.errorCount > 10 ? 'text-danger' : 'text-foreground'
                }`}>
                  {system?.errorCount}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Sync Now
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Icon name="Settings" size={16} className="mr-2" />
                Configure
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add New System */}
      <div className="bg-muted/50 rounded-lg border-2 border-dashed border-border p-8 text-center">
        <Icon name="Plus" size={48} className="text-muted-foreground mx-auto mb-3" />
        <h3 className="font-semibold text-foreground mb-2">Add New System</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Connect additional POS terminals, e-commerce platforms, or mobile applications
        </p>
        <Button>
          <Icon name="Plus" size={18} className="mr-2" />
          Add System
        </Button>
      </div>
    </div>
  );
};

export default SystemIntegration;